import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { isValidEmail } from '../../utils/helpers';
import { locationUtils } from '../../utils/locationUtils';
import { useAuth } from '../../hooks/useAuth';
import { trackRegistration } from '../../services/analyticsService';
import SuccessMessage from '../common/SuccessMessage';
import { encrypt } from '../../utils/encrypt';
import { SALESPINCH_API_URL, SHOW_PLAN_INFO } from '../../constants';
import { mapCountryCodeForDB } from '../../utils/helpers';
const initData = {
    heading: "Transform Sales with Intelligent, AI-Backed Lead Management",
    content: "SalesPinch is an AI-enhanced lead management platform built for high-performing sales teams. Capture, assign, monitor, and convert leads — all in one unified workspace, across mobile and web.",
    btnText: "Start Free Trial",
    formHeading: "Get Started with SalesPinch",
    formText: "Complete your details to create your SalesPinch workspace.",
    formBtn: "Create Account",
    formBtnText: "By signing up, you agree to our",
    formBtnText_2: "Terms of Service",
    formBtnText_3: "Privacy Policy"
  }
  
const HeroSection = () => {
    const history = useHistory();
    const showPlanInfo = SHOW_PLAN_INFO;
    const { register, error } = useAuth();
    
    const [data, setData] = useState({});
    const [formData, setFormData] = useState({
        client_name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        acceptTerms: false
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userCountry, setUserCountry] = useState(null);
    const [registrationError, setRegistrationError] = useState(null);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [clientData, setClientData] = useState(null);

    useEffect(() => {
        setData(initData);
        
        // Detect user's country for phone number suggestion
        const detectCountry = async () => {
            try {
                const countryData = await locationUtils.detectUserCountry();
                setUserCountry(countryData);
            } catch (error) {
                console.warn('Failed to detect country for phone suggestion:', error);
            }
        };
        
        detectCountry();
    }, []);

    // Helper function to get country calling code
    const getCountryCode = (countryCode) => {
        const countryCallingCodes = {
            'US': '1',
            'IN': '91',
            'CA': '1',
            'GB': '44',
            'AU': '61',
            'EU': '33', // France as default for EU
            'JP': '81',
            'CN': '86',
            'BR': '55',
            'MX': '52'
        };
        return countryCallingCodes[countryCode] || '1';
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.client_name.trim()) {
            errors.client_name = 'Company name is required';
        }

        if (!formData.contact_name.trim()) {
            errors.contact_name = 'Contact name is required';
        }

        if (!formData.contact_email.trim()) {
            errors.contact_email = 'Email is required';
        } else if (!isValidEmail(formData.contact_email)) {
            errors.contact_email = 'Please enter a valid email address';
        }

        if (!formData.contact_phone.trim()) {
            errors.contact_phone = 'Phone number is required';
        } else         if (!/^\+[1-9][0-9]{7,15}$/.test(formData.contact_phone)) {
            errors.contact_phone = 'Please enter a valid phone number with country code (e.g., +1 or +91)';
        }

        if (!formData.acceptTerms) {
            errors.acceptTerms = 'You must accept the Terms of Service and Privacy Policy to continue';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setRegistrationError(null);

        try {
            // Determine country code and currency based on user's selection or auto-detection
            let countryCode = 'US';
            let currencyCode = 'USD';
            
            // Check if user has explicitly selected a country (from CountrySelector)
            const cachedCountry = locationUtils.getCachedCountry();
            if (cachedCountry && cachedCountry.countryCode) {
                // User has selected a country
                if (cachedCountry.countryCode === 'IN') {
                    // India selected
                    countryCode = 'IN';
                    currencyCode = 'INR';
                } else if (cachedCountry.countryCode === 'GLOBAL') {
                    // Global selected - use US/USD
                    countryCode = 'US';
                    currencyCode = 'USD';
                } else {
                    // Other country selected - use the selected values
                    countryCode = mapCountryCodeForDB(cachedCountry.countryCode);
                    currencyCode = cachedCountry.currency || 'USD';
                }
            } else if (userCountry && userCountry.countryCode) {
                // No explicit selection - use auto-detected country
                // Only India gets special treatment, everything else defaults to US/USD
                if (userCountry.countryCode === 'IN') {
                    countryCode = 'IN';
                    currencyCode = 'INR';
                } else {
                    // All other countries default to US/USD
                    countryCode = 'US';
                    currencyCode = 'USD';
                }
            }
            // Otherwise, defaults to US/USD

            // Prepare registration data for free plan with country and currency info
            const registrationData = {
                ...formData,
                country_code: countryCode,
                currency_code: currencyCode,
                plan_data: {
                    plan_id: 1, // Free plan
                    plan_name: 'Free',
                    base_price: 0,
                    user_limit: 1,
                    ai_tokens_limit: 1000,
                    billing_cycle: 'Monthly'
                }
            };

            console.log('Registration data being sent:', registrationData);
            const result = await register(registrationData);
            
            if (result.success) {
                setClientData(result.data);
                
                // Track registration
                trackRegistration('Free');
                
                // Complete registration with SalesPinch API
                await completeRegistration(result.data);
            } else {
                setRegistrationError('Registration failed. Please try again or contact support if the issue persists.');
            }
        } catch (err) {
            console.error('Registration error:', err);
            setRegistrationError('An unexpected error occurred during registration. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const completeRegistration = async (clientData) => {
        try {
            // Determine country code and currency based on user's selection or auto-detection
            let countryCode = 'US';
            let currencyCode = 'USD';
            
            // Check if user has explicitly selected a country (from CountrySelector)
            const cachedCountry = locationUtils.getCachedCountry();
            if (cachedCountry && cachedCountry.countryCode) {
                // User has selected a country
                if (cachedCountry.countryCode === 'IN') {
                    // India selected
                    countryCode = 'IN';
                    currencyCode = 'INR';
                } else if (cachedCountry.countryCode === 'GLOBAL') {
                    // Global selected - use US/USD
                    countryCode = 'US';
                    currencyCode = 'USD';
                } else {
                    // Other country selected - use the selected values
                    countryCode = mapCountryCodeForDB(cachedCountry.countryCode);
                    currencyCode = cachedCountry.currency || 'USD';
                }
            } else if (userCountry && userCountry.countryCode) {
                // No explicit selection - use auto-detected country
                // Only India gets special treatment, everything else defaults to US/USD
                if (userCountry.countryCode === 'IN') {
                    countryCode = 'IN';
                    currencyCode = 'INR';
                } else {
                    // All other countries default to US/USD
                    countryCode = 'US';
                    currencyCode = 'USD';
                }
            }
            // Otherwise, defaults to US/USD

            const salespinchPayload = {
                client_name: clientData.client_name,
                first_name: formData.contact_name,
                mobile_no: clientData.contact_phone,
                email: clientData.contact_email,
                client_api_key: clientData.api_key,
                country_code: countryCode,
                currency_code: currencyCode,
                client_identifier: clientData.client_identifier,
            };
            console.log(salespinchPayload);
            const encryptedPayload = encrypt(JSON.stringify(salespinchPayload));
            
            const salespinchResponse = await fetch(`${SALESPINCH_API_URL}/clients`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ data: encryptedPayload }),
            });

            if (!salespinchResponse.ok) {
                const errorData = await salespinchResponse.json().catch(() => ({ message: 'Failed to sync with SalesPinch. Invalid JSON response.' }));
                throw new Error(errorData.message || 'Failed to sync with SalesPinch');
            }
            
            const salespinchResult = await salespinchResponse.json();

            if (salespinchResult.status !== 'success' || salespinchResult.data?.status !== 'success') {
                throw new Error(salespinchResult.message || salespinchResult.data?.message || 'SalesPinch API returned an error');
            }

            setRegistrationSuccess(true);
            setRegistrationError(null);

        } catch (err) {
            console.error("Failed to call salespinch api", err);
            setRegistrationError('Your account was created successfully, but there was an issue syncing with our systems. Please contact support with your API key to complete the setup.');
            // Still show success but with warning about sync issue
            setRegistrationSuccess(true);
        }
    };

    const handleContinueToDashboard = () => {
        // Redirect to dashboard or main application
        window.location.href = 'https://app.salespinch.com/dashboard';
    };

    const handleGoToPricing = () => {
        history.push('/pricing');
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear validation error when user starts typing
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Show success page when registration is successful
    if (registrationSuccess) {
        return (
            <section id="home" className="section welcome-area bg-overlay d-flex align-items-center">
                <div className="container">
                    <div className="row align-items-center justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <SuccessMessage
                                title="Registration Successful!"
                                message={
                                    <>
                                        {registrationError ? (
                                            <>
                                                ⚠️ {registrationError}
                                                <br /><br />
                                                Your API Key: <code>{clientData?.api_key}</code>
                                                <br /><br />
                                                Please contact support with this API key to complete your setup.
                                            </>
                                        ) : (
                                            <>
                                                ✅ Your SalesPinch account has been created successfully!
                                                <br /><br />
                                                We've sent your login credentials and activation details to <strong>{clientData?.contact_email}</strong>.
                                                <br /><br />
                                                📩 Please check your inbox (and spam folder just in case) for an email titled "Welcome to SalesPinch".
                                                <br /><br />
                                                Inside, you'll find:
                                                <br />
                                                ✅ Your temporary password
                                                <br />
                                                ✅ A link to log in and get started
                                            </>
                                        )}
                                    </>
                                }
                                apiKey={clientData?.api_key}
                                onContinue={handleContinueToDashboard}
                                onGoToPricing={handleGoToPricing}
                                showPricingButton={showPlanInfo}
                            />
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id="home" className="section welcome-area bg-overlay d-flex align-items-center">
            <div className="container">
                <div className="row align-items-center justify-content-center">
                    {/* Welcome Intro Start */}
                    <div className="col-12 col-lg-7">
                        <div className="welcome-intro">
                            <h1 className="text-white">{data.heading}</h1>
                            <p className="text-white my-4">{data.content}</p>
                            {/* Store Buttons */}
                            <div className="button-group store-buttons d-flex">
                                <a href="/signup" className="btn sApp-btn text-uppercase">{data.btnText}</a>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-8 col-lg-5">
                        {/* Contact Box */}
                        <div className="contact-box bg-white text-center rounded p-4 mt-5 shadow-lg">
                            {/* Contact Form */}
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="contact-top">
                                    <h3 className="contact-title">{data.formHeading}</h3>
                                    <h5 className="text-secondary fw-3 py-3">{data.formText}</h5>
                                </div>
                                
                                {/* Error Messages */}
                                {error && (
                                    <div className="alert alert-danger alert-sm">
                                        <small>{error}</small>
                                    </div>
                                )}
                                {registrationError && (
                                    <div className="alert alert-danger alert-sm">
                                        <small>{registrationError}</small>
                                    </div>
                                )}
                                

                                <div className="row mt-4">
                                    <div className="col-12">
                                        <div className="form-group">
                                            <div className="input-group">
                                                <div className="input-group-prepend">
                                                    <span className="input-group-text"><i className="fas fa-building" /></span>
                                                </div>
                                                <input 
                                                    type="text" 
                                                    className={`form-control ${validationErrors.client_name ? 'is-invalid' : ''}`}
                                                    name="client_name"
                                                    value={formData.client_name}
                                                    onChange={handleChange}
                                                    placeholder="Company Name" 
                                                    required="required" 
                                                />
                                            </div>
                                            {validationErrors.client_name && (
                                                <div className="invalid-feedback d-block">{validationErrors.client_name}</div>
                                            )}
                                        </div>
                                        <div className="form-group">
                                            <div className="input-group">
                                                <div className="input-group-prepend">
                                                    <span className="input-group-text"><i className="fas fa-user" /></span>
                                                </div>
                                                <input 
                                                    type="text" 
                                                    className={`form-control ${validationErrors.contact_name ? 'is-invalid' : ''}`}
                                                    name="contact_name"
                                                    value={formData.contact_name}
                                                    onChange={handleChange}
                                                    placeholder="Contact Name" 
                                                    required="required" 
                                                />
                                            </div>
                                            {validationErrors.contact_name && (
                                                <div className="invalid-feedback d-block">{validationErrors.contact_name}</div>
                                            )}
                                        </div>
                                        <div className="form-group">
                                            <div className="input-group">
                                                <div className="input-group-prepend">
                                                    <span className="input-group-text"><i className="fas fa-envelope-open" /></span>
                                                </div>
                                                <input 
                                                    type="email" 
                                                    className={`form-control ${validationErrors.contact_email ? 'is-invalid' : ''}`}
                                                    name="contact_email"
                                                    value={formData.contact_email}
                                                    onChange={handleChange}
                                                    placeholder="Email" 
                                                    required="required" 
                                                />
                                            </div>
                                            {validationErrors.contact_email && (
                                                <div className="invalid-feedback d-block">{validationErrors.contact_email}</div>
                                            )}
                                        </div>
                                        <div className="form-group">
                                            <div className="input-group">
                                                <div className="input-group-prepend">
                                                    <span className="input-group-text"><i className="fas fa-phone" /></span>
                                                </div>
                                                <input 
                                                    type="tel" 
                                                    className={`form-control ${validationErrors.contact_phone ? 'is-invalid' : ''}`}
                                                    name="contact_phone"
                                                    value={formData.contact_phone}
                                                    onChange={handleChange}
                                                    placeholder="Phone Number (with country code)"
                                                    required
                                                />
                                            </div>
                                            <small className="form-text text-muted">
                                                Please enter your phone number with country code 
                                                {userCountry && (
                                                    <span> (suggested: +{getCountryCode(userCountry.countryCode)})</span>
                                                )}
                                            </small>
                                            {validationErrors.contact_phone && (
                                                <div className="invalid-feedback d-block">{validationErrors.contact_phone}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="form-group mt-3">
                                            <div className="form-check">
                                                <input 
                                                    type="checkbox" 
                                                    className={`form-check-input ${validationErrors.acceptTerms ? 'is-invalid' : ''}`}
                                                    name="acceptTerms"
                                                    id="acceptTerms"
                                                    checked={formData.acceptTerms}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <label className="form-check-label" htmlFor="acceptTerms">
                                                    {data.formBtnText} <a href="/terms">{data.formBtnText_2}</a> &amp; <a href="/privacy">{data.formBtnText_3}</a>
                                                </label>
                                            </div>
                                            {validationErrors.acceptTerms && (
                                                <div className="invalid-feedback d-block">{validationErrors.acceptTerms}</div>
                                            )}
                                        </div>
                                        <button 
                                            className="btn btn-bordered w-100 mt-3 mt-sm-4" 
                                            type="submit"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                                    Creating Account...
                                                </>
                                            ) : (
                                                data.formBtn
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                            <p className="form-message" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default HeroSection;